import { AnyAction } from 'redux';
import { RequestAction } from '@redux-requests/core';

import { actionPrefixer } from 'shared/utils/utils';
import { Safelist, SafelistTerm, BlankSafelist, RedactableTerm } from 'shared/types/safelist';

import { CreateSafelistAction } from './types';

const prefix = actionPrefixer('SAFELIST');

export const FETCH_SAFELIST_SUMMARIES = prefix('FETCH_SAFELIST_SUMMARIES');
export const FETCH_SAFELIST_BY_GUID = prefix('FETCH_SAFELIST_BY_GUID');
export const MIGRATE_SAFELISTS = prefix('MIGRATE_SAFELISTS');

export const CREATE_SAFELIST = prefix('CREATE_SAFELIST');

export const PATCH_SAFELIST = prefix('PATCH_SAFELIST');

export const DELETE_SAFELIST = prefix('DELETE_SAFELIST');

export const EXTRACT_TERMS_FROM_TEXT = prefix('EXTRACT_TERMS_FROM_TEXT');
export const CLEAR_EXTRACTED_TERMS = prefix('CLEAR_EXTRACTED_TERMS');
export const ADD_MANUAL_EXTRACTED_TERM = prefix('ADD_MANUAL_EXTRACTED_TERM'); // Manually added terms are also 'extracted terms'
export const UPDATE_EXTRACTED_TERM = prefix('UPDATE_EXTRACTED_TERM'); // Extracted and manually added terms can be updated
export const REMOVE_EXTRACTED_TERM = prefix('REMOVE_EXTRACTED_TERM'); 

// Since the API doesn't support creating a safelist with a list of terms, this action
// involves creating a safelist along with an update to add a list of terms to it.
export const ADD_SAFELIST = prefix('ADD_SAFELIST');

export const START_SAFELIST_EDITING = prefix('START_SAFELIST_EDITING');
export const RESET_SAFELIST_EDIT_DATA = prefix('RESET_SAFELIST_EDIT_DATA');

export const DELETE_SAFELIST_TERM = prefix('DELETE_SAFELIST_TERM');

export const fetchSafelistSummaries = (): RequestAction => ({
  type: FETCH_SAFELIST_SUMMARIES,
  request: {
    url: '/services/safelist-libraries',
  },
});

export const fetchSafelistByGuid = (safelistGuid: string): RequestAction => ({
  type: FETCH_SAFELIST_BY_GUID,
  request: {
    url: `/services/safelist-libraries/${safelistGuid}`,
  },
});

export const createSafelist = (safelistName: string): RequestAction => ({
  type: CREATE_SAFELIST,
  request: {
    url: '/services/safelist-libraries',
    method: 'POST',
    body: {
      name: safelistName,
    },
  },
});

export const patchSafelist = (
  safelistGuid: Safelist['guid'],
  terms: SafelistTerm[],
): RequestAction => ({
  type: PATCH_SAFELIST,
  request: {
    url: `/services/safelist-libraries/${safelistGuid}`,
    method: 'PATCH',
    body: {
      entries: terms,
    },
  },
});

export const deleteSafelist = (safelistGuid: Safelist['guid']) => ({
  type: DELETE_SAFELIST,
  payload: {
    safelistGuid,
  },
});

export const deleteSafelistTerm = (
  safelistGuid: Safelist['guid'],
  termGuid: SafelistTerm['guid'],
) => ({
  type: DELETE_SAFELIST_TERM,
  payload: {
    safelistGuid,
    termGuid,
  },
});

export const extractTermsFromText = (text: string): RequestAction => ({
  type: EXTRACT_TERMS_FROM_TEXT,
  request: {
    url: '/services/safelist-libraries/extract',
    method: 'POST',
    body: text,
    headers: {
      ['Content-Type']: 'application/json',
    },
  },
  meta: {
    onSuccess: (response) => ({ data: response }),
  },
});

export const addTermManuallyToExtractedTerms = (term: RedactableTerm): AnyAction => ({
  type: ADD_MANUAL_EXTRACTED_TERM,
  payload: term,
});

/**
 * Called when after safelist term gets edited
 * @param term 
 * @returns 
 */
export const updateSafelistTerm = (originalTerm: RedactableTerm, updatedTerm: RedactableTerm): AnyAction => ({
  type: UPDATE_EXTRACTED_TERM,
  payload: {originalTerm, updatedTerm},
});
/**
 * Remove an individual term, usually used for new terms that cancelled and not saved
 * @param term 
 * @returns 
 */
export const removeExtractedTerm = (termToRemove: RedactableTerm): AnyAction => ({
  type: REMOVE_EXTRACTED_TERM,
  payload: termToRemove,
});

export const clearExtractedTerms = (): AnyAction => ({
  type: CLEAR_EXTRACTED_TERMS,
});

export const addSafelist = (safelist: BlankSafelist): CreateSafelistAction => ({
  type: ADD_SAFELIST,
  payload: safelist,
});

export const startSafelistEditing = (safelistGuid: Safelist['guid']): AnyAction => ({
  type: START_SAFELIST_EDITING,
  payload: { safelistGuid },
});

export const resetSafelistEditData = (): AnyAction => ({
  type: RESET_SAFELIST_EDIT_DATA,
});

export const migrateSafelists = (): RequestAction => ({
  type: MIGRATE_SAFELISTS,
  request: {
    url: '/services/safelist-libraries/migrate-company-whitelist',
    method: 'POST',
  },
});
